import {
  Box,
  Button as MuiButton,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  AutocompleteInput,
  CreateContextProvider,
  FileField,
  FileInput,
  Form,
  required,
  SaveButton,
  SelectInput,
  useCreateController,
  useNotify,
  useRefresh,
  useTranslate,
} from 'react-admin';
import CustomTagInput from '../../../components/CustomTagInput';
import {
  importProxiesCsv,
  listProxyProviders,
  PROXY_PROTOCOL_CHOICES,
  PROXY_TIER_CHOICES,
} from '../../../services/proxyManagerService';
import { Resources } from '../../resource';
import { PermissionButton } from 'src/routes/components/permission-button';

const RESOURCE = Resources.proxyManagerProxies;

const atLeastOneTag = (value: any) =>
  Array.isArray(value) && value.length > 0
    ? undefined
    : 'module.proxyManager.validate.tagsRequired';

const downloadCsvTemplate = () => {
  const csv = 'host;port;username;password';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'proxies-template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const ImportCsvDialog = ({ onClose }: { onClose: () => void }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const t = useTranslate();
  const [providerChoices, setProviderChoices] = useState<{ id: string; name: string }[]>([]);

  const controller = useCreateController({ resource: RESOURCE });

  useEffect(() => {
    let mounted = true;
    listProxyProviders()
      .then((providers) => {
        if (!mounted) return;
        setProviderChoices(
          providers.map((p) => ({ id: p.name, name: `${p.name} (${p.proxyCount})` }))
        );
      })
      .catch(() => {
        if (mounted) setProviderChoices([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const handleSubmit = async (values: any) => {
    const file = values?.file?.rawFile as File | undefined;
    if (!file) {
      notify(t('module.proxyManager.validate.fileRequired'), { type: 'error' });
      return;
    }

    try {
      await importProxiesCsv(file, {
        protocol: values.protocol,
        source: values.source,
        tags: values.tags ?? [],
        tier: values.tier || undefined,
      });
      notify(t('module.proxyManager.importSuccess'), { type: 'success', autoHideDuration: 1500 });
      refresh();
      onClose();
    } catch (error: any) {
      notify(
        error?.response?.data?.message || error?.message || t('module.proxyManager.unknownError'),
        { type: 'error' }
      );
    }
  };

  return (
    <CreateContextProvider value={{ ...controller, save: handleSubmit }}>
      <Form
        onSubmit={handleSubmit}
        defaultValues={{ protocol: 'HTTP', tags: [] }}
        disableInvalidFormNotification
      >
        <DialogTitle>
          {t('module.proxyManager.importCsv')}
          <Typography variant="body2" color="textSecondary" mt={1}>
            {t('module.proxyManager.importCsvHint')}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <SelectInput
              source="protocol"
              label="module.proxyManager.protocol"
              choices={[...PROXY_PROTOCOL_CHOICES]}
              defaultValue="HTTP"
              validate={[required()]}
              fullWidth
            />
            <AutocompleteInput
              source="source"
              label="module.proxyManager.source"
              choices={providerChoices}
              validate={[required()]}
              fullWidth
              onCreate={(filter) => {
                const value = (filter ?? '').trim();
                if (!value) return undefined;
                const existing = providerChoices.find((c) => c.id === value);
                if (existing) return existing;
                const newOption = { id: value, name: value };
                setProviderChoices((prev) => [...prev, newOption]);
                return newOption;
              }}
            />
            <SelectInput
              source="tier"
              label="module.proxyManager.tier"
              choices={[...PROXY_TIER_CHOICES]}
              resettable
              fullWidth
            />
            <CustomTagInput
              source="tags"
              label={t('module.proxyManager.tags')}
              defaultValue={[]}
              validate={atLeastOneTag}
            />
            <Box mt={2}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <MuiButton variant="text" size="small" onClick={downloadCsvTemplate}>
                  {t('module.proxyManager.downloadTemplate')}
                </MuiButton>
              </Box>
              <FileInput
                label="module.proxyManager.csvFile"
                source="file"
                accept={{ 'text/csv': ['.csv'] }}
                multiple={false}
                validate={required()}
              >
                <FileField source="src" title="title" />
              </FileInput>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <MuiButton variant="outlined" onClick={onClose}>
            {t('actions.cancel')}
          </MuiButton>
          <PermissionButton resource="proxy-manager" action="create">
            <SaveButton label="module.proxyManager.import" icon={<></>} />
          </PermissionButton>
        </DialogActions>
      </Form>
    </CreateContextProvider>
  );
};

export default ImportCsvDialog;
